// Батарейки и сроки — что и когда пора менять на судах. ТОЛЬКО ЧТЕНИЕ.
//
// ЧТО ЭТОТ ЭКРАН ОТВЕЧАЕТ. Один вопрос, ради которого его и открывают: по какому судну
// уже просрочена батарея, гидростат или береговое обслуживание — и по какому просрочится
// в ближайшие месяцы. Это повод позвонить судовладельцу до того, как позвонит Регистр.
//
// ОТКУДА ДАННЫЕ. Из тех же 2 630 актов на оборудование (на 28.07.2026), что и в соседнем
// разделе, только разрезанных иначе: берётся последний акт КАЖДОГО ТИПА по судну и из него
// достаются все записанные сроки. Новых данных раздел не требует — он целиком вывод
// поверх имеющихся
// (см. p73_equip_due.py).
//
// ТРИ ВЕЩИ, О КОТОРЫХ ЭКРАН ОБЯЗАН СКАЗАТЬ ВСЛУХ, иначе он врёт:
//   1. Пороги «скоро» и «в течение года» — НАШИ. На портале 73 сроки просто записаны и
//      ни с чем не сравниваются; функции «просрочено» там нет вовсе. Поэтому пороги
//      приезжают числом в ответе ядра и печатаются на экране, а не зашиты молча.
//   2. Точность у большинства сроков — МЕСЯЦ: инженер переписывает то, что напечатано на
//      батарее. Показывать «30.06.2029» вместо «06.2029» значит придумать день.
//   3. Судна, по которому актов нет, здесь нет ВОВСЕ. Пустая строка читалась бы как
//      «сроков нет», то есть «всё в порядке», а это прямо противоположно правде.
//
// ЧЕГО ЗДЕСЬ НЕТ. Ни одной кнопки, которая что-то меняет: акты заполняют на портале 73.
// И ни одного «пора менять» от себя: у АКБ, например, в данных есть только год установки
// и остаточная ёмкость, а норматива замены нет — поэтому АКБ показан как факт, а не как
// просрочка.
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useСрокиОборудования } from '../api/p73'
import type { СводкаСроков, СрокСтрока, ФильтрыP73 } from '../api/p73'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtDays, pluralN } from '../lib/format'
import { ПУТИ, названиеШаблона } from '../lib/p73'
import {
  ПОРЯДОК_СОСТОЯНИЙ, РАЗМЕР_СТРАНИЦЫ, СОСТОЯНИЯ, бейджСостояния, названиеСостояния,
  осталосьСловами, срокСловами,
} from '../lib/p73due'

export default function EquipDue() {
  const [q, setQ] = useState('')
  const [kind, setKind] = useState('')
  const [status, setStatus] = useState('')
  const [actType, setActType] = useState('')
  const [sort, setSort] = useState('due')
  const [start, setStart] = useState(0)

  const фильтры = useMemo<ФильтрыP73>(() => ({
    q: q.trim() || undefined,
    kind: kind || undefined,
    status: status || undefined,
    act_type: actType || undefined,
    sort,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [q, kind, status, actType, sort, start])

  const запрос = useСрокиОборудования(фильтры)
  const данные = запрос.data

  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  return (
    <>
      <div className="page-head">
        <h1>Батарейки и сроки</h1>
        <span className="spacer" />
        <span className="hint">
          {данные
            ? `на ${fmtDate(данные.as_of)} · судов в сводке: ${данные.vessels}`
            : ''}
        </span>
      </div>

      <div className="notice">
        <b>«Просрочено» здесь — наш вывод, а не отметка Регистра.</b> В актах портала 73
        сроки просто записаны и ни с чем не сравниваются. Мы сравниваем их с сегодняшним
        днём{данные ? ' и считаем «скоро» всё, что истекает в ближайшие'
          + ` ${fmtDays(данные.thresholds.soon_days)}`
          : ''}. Дата, до которой судно допущено, определяется Регистром, а не этим экраном.
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {данные && (
          <>
            <Плитки сводка={данные} выбрано={status} выбрать={(к) => сСброса(setStatus)(к)} />

            <div className="card" style={{ marginBottom: 16 }}>
              <FilterBar id="equip-due">
                <div className="f" style={{ flex: 2, minWidth: 220 }}>
                  <label htmlFor="edq">Судно (название или IMO)</label>
                  <input id="edq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                         placeholder="например: Дон Стар или 9317016" />
                </div>

                <div className="f">
                  <label htmlFor="edk">Что за срок</label>
                  <select id="edk" value={kind} onChange={(e) => сСброса(setKind)(e.target.value)}>
                    <option value="">любой</option>
                    {Object.entries(данные.kinds).map(([к, имя]) => (
                      <option key={к} value={к}>{имя}</option>
                    ))}
                  </select>
                </div>

                <div className="f">
                  <label htmlFor="eds">Состояние</label>
                  <select id="eds" value={status} onChange={(e) => сСброса(setStatus)(e.target.value)}>
                    <option value="">любое</option>
                    {ПОРЯДОК_СОСТОЯНИЙ.map((к) => (
                      <option key={к} value={к}>
                        {СОСТОЯНИЯ[к].название} ({данные.summary[к] ?? 0})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="f">
                  <label htmlFor="edt">Из какого акта</label>
                  <select id="edt" value={actType}
                          onChange={(e) => сСброса(setActType)(e.target.value)}>
                    <option value="">любого</option>
                    {Object.entries(данные.act_types)
                      .sort((a, b) => b[1] - a[1])
                      .map(([к, n]) => (
                        <option key={к} value={к}>{названиеШаблона(к)} ({n})</option>
                      ))}
                  </select>
                </div>

                <div className="f">
                  <label htmlFor="edo">Порядок</label>
                  <select id="edo" value={sort} onChange={(e) => сСброса(setSort)(e.target.value)}>
                    <option value="due">сначала то, что горит</option>
                    <option value="date">строго по дате</option>
                    <option value="vessel">по судну</option>
                  </select>
                </div>
              </FilterBar>

              <div className="hint" style={{ marginTop: 10 }}>
                Считается по последнему акту каждого типа на судне. Сами акты —{' '}
                <Link to={ПУТИ.акты}>в разделе «Акты на оборудование»</Link>: там видно бланк
                целиком и скан оригинала.
              </div>
            </div>

            <Таблица сводка={данные} />

            {данные.total > РАЗМЕР_СТРАНИЦЫ && (
              <div className="pager">
                <button disabled={start === 0 || запрос.isFetching}
                        onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>← назад</button>
                <span className="muted">
                  {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, данные.total)} из {данные.total}
                </span>
                <button disabled={данные.next === null || запрос.isFetching}
                        onClick={() => setStart(данные.next!)}>вперёд →</button>
              </div>
            )}

            <Границы сводка={данные} />
          </>
        )}
      </QueryState>
    </>
  )
}

// ═══════════════════════════════ плитки состояний ═══════════════════════════════

function Плитки({ сводка, выбрано, выбрать }: {
  сводка: СводкаСроков
  выбрано: string
  выбрать: (к: string) => void
}) {
  return (
    <div className="tiles" style={{ marginBottom: 16 }}>
      {ПОРЯДОК_СОСТОЯНИЙ.map((к) => (
        <button key={к} className="tile" type="button"
                aria-pressed={выбрано === к}
                title={СОСТОЯНИЯ[к].смысл}
                onClick={() => выбрать(выбрано === к ? '' : к)}>
          {/* Подпись плитки здесь цветная — состояние читается цветом раньше, чем словом,
              поэтому вместо `.t-label` стоит бейдж. Число же — обычное `.t-sum`, как во
              всех плитках CRM: свой размер и свой вес рядом с ним были бы тем же самым,
              но мимо токенов (styles.css §7). */}
          <div className={`badge ${бейджСостояния(к)}`}>{СОСТОЯНИЯ[к].название}</div>
          <div className="t-sum">{сводка.summary[к] ?? 0}</div>
        </button>
      ))}
    </div>
  )
}

// ═══════════════════════════════ таблица сроков ═══════════════════════════════

function Таблица({ сводка }: { сводка: СводкаСроков }) {
  return (
    <div className="card">
      {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12).
          Экран смотрят с судна и из машины, поэтому подписи в data-label обязательны. */}
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Судно</th>
              <th>Что</th>
              <th className="nowrap">Как записано в акте</th>
              <th className="nowrap">Состояние</th>
              <th className="nowrap">Осталось</th>
              <th className="nowrap">Откуда</th>
            </tr>
          </thead>
          <tbody>
            {сводка.rows.map((с, i) => <Строка key={`${с.act_d1_id}-${с.label}-${i}`} с={с} />)}
            {сводка.rows.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  <div className="empty-title">Под эти фильтры сроков нет</div>
                  <div className="empty-hint">
                    Это ответ про фильтры, а не про суда: снимите условие, и сводка вернётся.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Строка({ с }: { с: СрокСтрока }) {
  return (
    <tr>
      <td data-label="Судно">
        {/* Ссылки на карточку судна CRM здесь нет намеренно: в акте лежит IMO и название
            текстом, мостика к контакту Bitrix у актов не существует
            (на 28.07.2026 в справочнике портала нет 40 IMO из 761). Ссылка в никуда
            хуже её отсутствия. */}
        {с.vessel_name || <span className="muted">без названия</span>}
        {с.imo && <div className="muted mono">IMO {с.imo}</div>}
      </td>
      <td data-label="Что">
        {с.label}
        {с.note && <div className="hint">{с.note}</div>}
      </td>
      {/* Первоисточник — то, что написал инженер. Разобранную дату дописываем рядом только
          когда разбор что-то добавил: у «24.03.2023» добавлять нечего. */}
      <td className="nowrap" data-label="Как записано в акте">
        <span className="mono">{срокСловами(с.value, с.due, с.precision)}</span>
        {с.precision === 'month' && <div className="hint">точность — месяц</div>}
        {с.precision === 'year' && <div className="hint">точность — год</div>}
      </td>
      <td className="nowrap" data-label="Состояние">
        <span className={`badge ${бейджСостояния(с.status)}`} title={СОСТОЯНИЯ[с.status]?.смысл}>
          {названиеСостояния(с.status)}
        </span>
      </td>
      <td className="nowrap" data-label="Осталось">
        {с.direction === 'срок'
          ? осталосьСловами(с.days_left, с.precision)
          : <span className="muted">не срок</span>}
      </td>
      <td className="nowrap muted" data-label="Откуда">{с.source}</td>
    </tr>
  )
}

// ═══════════════════════════════ границы данных ═══════════════════════════════

/**
 * Раздел, который молчит про охват и возраст данных, однажды введёт в заблуждение.
 * Здесь это особенно верно: отсутствие судна в сводке означает «актов по нему нет», а
 * человек прочтёт «сроки в порядке».
 */
function Границы({ сводка }: { сводка: СводкаСроков }) {
  const b = сводка.bounds
  const без = Math.max(0, (b.vessels_known ?? 0) - сводка.vessels)
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h2>Границы этих данных</h2>
      <ul className="plain-list">
        <li>
          Считано по <b>{b.acts_done}</b> завершённым актам за период{' '}
          {fmtDate(b.act_from)} — {fmtDate(b.act_to)}. Черновиков ({b.acts_draft}) в сводке нет:
          так же их отбрасывает и портал 73.
        </li>
        <li>
          Судов в сводке — <b>{сводка.vessels}</b>: это те, по которым есть хотя бы один акт.
          В справочнике судов портала их {b.vessels_known}; про остальные {без} сводке сказать
          нечего, и их отсутствие здесь <b>не означает</b>, что у них всё в порядке.
        </li>
        <li>
          Берётся последний акт каждого типа. Более ранние акты того же типа по тому же судну
          в расчёт не идут — но и не пропадают: они видны{' '}
          <Link to={ПУТИ.акты}>в разделе актов</Link>.
        </li>
        <li>
          Зеркало обновлено {fmtDate(b.last_sync_at || b.synced_at, true)}. Счётчик синка
          показывает {b.last_count === null || b.last_count === undefined
            ? '— строк' : pluralN(b.last_count, 'строку', 'строки', 'строк')} при
          {' '}{b.acts_live} в зеркале — расхождение
          в единицы строк здесь нормально: счётчик пишется до досылки последней страницы.
        </li>
        <li className="muted">
          Пороги: «скоро» — {fmtDays(сводка.thresholds.soon_days)}, «в течение года» —{' '}
          {сводка.thresholds.year_days}. Оба наши; в источнике порогов нет.
        </li>
      </ul>

      <h3>Что означает каждое состояние</h3>
      <dl className="dl">
        {ПОРЯДОК_СОСТОЯНИЙ.map((к) => (
          <div key={к} style={{ display: 'contents' }}>
            <dt>{СОСТОЯНИЯ[к].название}</dt>
            <dd>{СОСТОЯНИЯ[к].смысл} · {сводка.summary[к] ?? 0}</dd>
          </div>
        ))}
      </dl>

      <h3>Оговорки ядра</h3>
      <ul className="plain-list">
        {Object.entries(сводка.data_notes).map(([к, текст]) => (
          <li key={к} className="muted">{текст}</li>
        ))}
      </ul>
    </div>
  )
}
